import { useEffect } from "react";
import { sortBy } from "lodash";
import eventEmitter, { EVENTS } from "src/services/eventEmitter";
import { MEMBERSHIP_EVENTS } from "src/components/Person/events";

export const useUpdateTeamStoreListener = ({
  mergePartialData,
  state,
  setState,
  loadTeamDataById,
  reloadOrgChart,
}) => {
  // GROUP_RENAMED
  useEffect(() => {
    const handler = (data) => {
      const team = state.teams[data?.id];

      mergePartialData({
        teams: {
          [team.id]: {
            ...team,
            name: data.name,
            hierarchy: data.hierarchy,
          },
        },
      });
    };

    eventEmitter.on(EVENTS.GROUP_RENAMED, handler);

    return () => eventEmitter.off(EVENTS.GROUP_RENAMED, handler);
  }, [state, mergePartialData]);

  // NEW_GROUP_ADDED
  useEffect(() => {
    const handler = (newGroup, proxy, variables) => {
      if (!variables?.parentId) {
        return;
      }

      const team = state.teams[variables?.parentId];

      if (!team) {
        return;
      }

      const childTeams = sortBy([...(team.childTeams || []), newGroup], "name");

      mergePartialData({
        teams: {
          [newGroup.id]: { ...newGroup, directParentId: team.id },
          [team.id]: {
            ...team,
            directChildTeamCount: (team?.directChildTeamCount || 0) + 1,
            childTeams,
          },
        },
      });
    };

    eventEmitter.on(EVENTS.NEW_GROUP_ADDED, handler);

    return () => eventEmitter.off(EVENTS.NEW_GROUP_ADDED, handler);
  }, [state, mergePartialData]);

  // GROUP_MOVED
  useEffect(() => {
    const handler = (fromParentId, toParentId, group) => {
      if (fromParentId && toParentId) {
        if (
          state.teams[toParentId] &&
          ![toParentId, fromParentId].includes(state.currentViewTeam?.id)
        ) {
          loadTeamDataById(toParentId);
        }

        setState((prevState) => {
          const fromParentTeam = prevState.teams[fromParentId];
          const toParentTeam = prevState.teams[toParentId];

          const nextTeamsState = { ...prevState.teams, [group.id]: group };

          if (fromParentTeam) {
            nextTeamsState[fromParentTeam.id] = {
              ...fromParentTeam,
              childTeams: fromParentTeam.childTeams.filter(
                (team) => team.id !== group.id
              ),
            };
          }

          if (toParentTeam) {
            nextTeamsState[toParentTeam.id] = {
              ...toParentTeam,
              childTeams: [...(toParentTeam.childTeams || []), group],
            };
          }
          return {
            ...prevState,
            teams: nextTeamsState,
          };
        });
      }
    };

    eventEmitter.on(EVENTS.GROUP_MOVED, handler);

    return () => eventEmitter.off(EVENTS.GROUP_MOVED, handler);
  }, [
    setState,
    mergePartialData,
    loadTeamDataById,
    state.teams,
    state.currentViewTeam?.id,
  ]);

  // GROUP_REMOVED
  useEffect(() => {
    const handler = () => {
      reloadOrgChart?.();
    };

    eventEmitter.on(EVENTS.GROUP_REMOVED, handler);

    return () => eventEmitter.off(EVENTS.GROUP_REMOVED, handler);
  }, [reloadOrgChart]);

  // GROUP_ATTRS_UPDATED
  useEffect(() => {
    const handler = (data) => {
      const team = state.teams[data?.id];

      mergePartialData({
        teams: {
          [team.id]: {
            ...team,
            attributes: data.attributes,
          },
        },
      });
    };

    eventEmitter.on(EVENTS.GROUP_ATTRS_UPDATED, handler);

    return () => eventEmitter.off(EVENTS.GROUP_ATTRS_UPDATED, handler);
  }, [state, mergePartialData]);

  // LOAD_MORE_MEMBERS
  useEffect(() => {
    const handler = (teamId, loadedItemCount) => {
      loadTeamDataById(teamId, null, {
        fetchMembers: true,
        memberOffset: loadedItemCount,
        fetchTeams: false,
        teamOffset: 0,
      });
    };

    eventEmitter.on(EVENTS.LOAD_MORE_MEMBERS, handler);

    return () => eventEmitter.off(EVENTS.LOAD_MORE_MEMBERS, handler);
  }, [loadTeamDataById]);

  // LOAD_MORE_TEAMS
  useEffect(() => {
    const handler = (teamId, loadedItemCount) => {
      loadTeamDataById(teamId, null, {
        fetchMembers: false,
        memberOffset: 0,
        fetchTeams: true,
        teamOffset: loadedItemCount,
      });
    };

    eventEmitter.on(EVENTS.LOAD_MORE_TEAMS, handler);

    return () => eventEmitter.off(EVENTS.LOAD_MORE_TEAMS, handler);
  }, [loadTeamDataById]);

  // MEMBERSHIP_CHANGED - reload org chart when a person is allocated/unallocated
  useEffect(() => {
    const handler = () => {
      reloadOrgChart?.();
    };

    eventEmitter.on(MEMBERSHIP_EVENTS.MEMBERSHIP_CHANGED, handler);

    return () =>
      eventEmitter.off(MEMBERSHIP_EVENTS.MEMBERSHIP_CHANGED, handler);
  }, [reloadOrgChart]);
};
